import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface TrainingPosition {
  id: string;
  user_id: string;
  game_session_id?: string;
  position_fen: string;
  correct_move_uci: string;
  correct_move_san: string;
  player_color: 'white' | 'black';
  mistake_type: string;
  position_category: string;
  difficulty_rating: number;
  evaluation_loss: number;
  times_attempted: number;
  times_solved: number;
  mastery_level: number;
  next_review_date: string;
  ease_factor: number;
  tags?: string[];
  is_archived: boolean;
  created_at: string;
}

export function useTrainingPositions() {
  const [positions, setPositions] = useState<TrainingPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadPositions = useCallback(async (filters?: {
    mistakeType?: string;
    category?: string;
    masteryLevel?: number;
    dueForReview?: boolean;
  }) => {
    setIsLoading(true);
    try {
      let query = (supabase as any)
        .from('training_positions')
        .select('*')
        .eq('is_archived', false)
        .order('next_review_date', { ascending: true });

      if (filters?.mistakeType) {
        query = query.eq('mistake_type', filters.mistakeType);
      }
      if (filters?.category) {
        query = query.eq('position_category', filters.category);
      }
      if (filters?.masteryLevel !== undefined) {
        query = query.eq('mastery_level', filters.masteryLevel);
      }
      if (filters?.dueForReview) {
        query = query.lte('next_review_date', new Date().toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setPositions((data as any) || []);
    } catch (error) {
      console.error('Error loading training positions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load training positions',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const extractFromGame = useCallback(async (gameSessionId: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await (supabase as any).rpc('extract_training_positions', {
        _game_session_id: gameSessionId
      });

      if (error) throw error;

      const count = (data as any)?.[0]?.positions_created || 0;
      
      toast({
        title: 'Success',
        description: `Extracted ${count} training position${count !== 1 ? 's' : ''} from this game`,
      });

      await loadPositions();
      return count;
    } catch (error: any) {
      console.error('Error extracting positions:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to extract training positions',
        variant: 'destructive',
      });
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [loadPositions]);

  const updatePosition = useCallback(async (
    positionId: string,
    updates: Partial<TrainingPosition>
  ) => {
    try {
      const { error } = await (supabase as any)
        .from('training_positions')
        .update(updates)
        .eq('id', positionId);

      if (error) throw error;

      setPositions(prev =>
        prev.map(p => p.id === positionId ? { ...p, ...updates } : p)
      );
    } catch (error) {
      console.error('Error updating position:', error);
      throw error;
    }
  }, []);

  const archivePosition = useCallback(async (positionId: string) => {
    try {
      await updatePosition(positionId, { is_archived: true });
      setPositions(prev => prev.filter(p => p.id !== positionId));
      
      toast({
        title: 'Success',
        description: 'Position archived',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive position',
        variant: 'destructive',
      });
    }
  }, [updatePosition]);

  const getStats = useCallback(() => {
    const total = positions.length;
    const mastered = positions.filter(p => p.mastery_level >= 4).length;
    const needsReview = positions.filter(p => 
      new Date(p.next_review_date) <= new Date()
    ).length;
    const byType = positions.reduce((acc, p) => {
      acc[p.mistake_type] = (acc[p.mistake_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { total, mastered, needsReview, byType };
  }, [positions]);

  return {
    positions,
    isLoading,
    loadPositions,
    extractFromGame,
    updatePosition,
    archivePosition,
    getStats
  };
}
